import React, { useState } from 'react';
import { motion } from 'framer-motion';
import MessageInput from './MessageInput';
import LogoIcon from './LogoIcon';

interface FuturisticHomeLayoutProps {
  onSendMessage: (message: string) => void;
  onStartChat: (initialPrompt?: string) => void;
}

const FuturisticHomeLayout: React.FC<FuturisticHomeLayoutProps> = ({ 
  onSendMessage,
  onStartChat
}) => {
  const handleSendMessage = (message: string) => {
    onSendMessage(message);
    onStartChat(message);
  };

  return (
    <div className="w-full h-full flex flex-col items-center justify-center relative">
      {/* Video Background */}
      <video 
        autoPlay 
        loop 
        muted 
        className="absolute inset-0 w-full h-full object-cover z-0"
        src="/videobg.mp4"
      />
      
      {/* Add gradient border container with glassmorphism */}
      <motion.div 
        className="relative w-full h-full flex items-center justify-center px-4 py-8"
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        {/* Main background with enhanced glassmorphism - much more transparent */}
        <div className="absolute inset-0 rounded-3xl bg-[#0d0d0e]/10 backdrop-blur-[6px] z-[1]"></div>
        
        {/* Thin gradient border (2px) */}
        <div className="absolute inset-0 rounded-3xl overflow-hidden z-[2]" style={{ padding: '2px' }}>
          <div className="absolute inset-[2px] rounded-[calc(1.5rem-2px)] bg-transparent"></div>
        </div>
        
        {/* 3D shadow effect */}
        <div className="absolute -bottom-4 inset-x-8 h-[20px] bg-black/40 blur-xl rounded-full -z-5"></div>
        
        <motion.div 
          className="w-full max-w-3xl flex flex-col items-center gap-8 relative z-[5] p-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          style={{ transform: 'translateZ(20px)' }} // 3D effect
        >
          {/* Logo Icon - direct with no container div */}
          <LogoIcon size={160} className="relative z-10 mb-2" />
          
          {/* Welcome Text with updated styling for "AI+" */}
          <div className="text-center mb-4 z-10">
            <h1 className="text-3xl sm:text-4xl font-adrianna-light text-white mb-3 font-weight-light">
              Obsidian<span style={{ 
                fontFamily: 'adrianna-extended, sans-serif',
                fontWeight: 700,
                fontStyle: 'italic'
              }}>AI+</span>
            </h1>
            <p className="text-lg sm:text-xl text-white font-light">How can I help you today?</p>
          </div>
          
          {/* Message Input in Grok Style - with more transparency */}
          <div className="w-full max-w-2xl z-10 px-3 sm:px-6">
            <div className="rounded-full p-1 sm:p-2 relative">
              <MessageInput 
                onSendMessage={handleSendMessage} 
                isGenerating={false}
                onGenerateImage={(imageUrls, prompt) => {
                  // Start a new chat with the generated images
                  onStartChat(prompt);
                  // Wait a bit for chat to initialize before handling the images
                  setTimeout(() => {
                    // We'll pass the prompt to onSendMessage to ensure a new chat starts
                    // The actual handling of images will be done in ChatPage
                    onSendMessage(`Generated images: ${prompt}`);
                  }, 100);
                }}
              />
            </div>
          </div>
          
          {/* Motivational Text with Badge */}
          <motion.div 
            className="w-full text-center z-10 mt-4 px-4 sm:px-6 flex flex-col items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <p className="text-xs sm:text-sm text-white font-light mb-3">
              Experience the future of conversational AI with vision and web search capabilities featuring enhanced insights.
            </p>
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default FuturisticHomeLayout;

// Add this at the end of the file or in your CSS
// @keyframes shimmer {
//   0% { background-position: -200% 0; }
//   100% { background-position: 200% 0; }
// }
